/**
 * Clients — the agency roster. Who each client is, where they stand (setup,
 * connections, costs, data freshness), your private notes on them, and the
 * one-move provisioning flow: create the account AND the owner's invite link
 * in one go, ready to paste into an email.
 *
 * Operator-gated server-side; the nav entry only shows for operators too.
 * Numbers come from the same /admin/portfolio fan-out the Portfolio page uses
 * (30-day window here) so this list can never disagree with that one.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { Card } from "../components/cards.js";
import { useAccountChange } from "../lib/account-context.js";
import { invitationExpiryCopy } from "../lib/invitation-expiry.js";

interface GeneratedInvite {
  url: string;
  expiresAt: string | null;
  providerKind: "clerk_application_invitation" | "clerk_sign_in_token" | null;
}

interface PortfolioRow {
  accountId: string;
  name: string;
  isDemo: boolean;
  createdAt: string | null;
  themeColorHex: string | null;
  revenue: number;
  onboardingCompleted: boolean;
  onboardingStep: number;
  costsCoveragePct: number | null;
  hasCosts: boolean;
  connectionsActive: number;
  connectionsBroken: number;
  lastSyncAt: string | null;
  syncStale: boolean;
  health: "good" | "warn" | "bad";
}

interface Portfolio {
  rows: PortfolioRow[];
  failedAccounts: Array<{ accountId: string; name: string }>;
}

export default function AdminPage(): React.ReactElement {
  const api = useApiClient();
  const changeAccount = useAccountChange();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [generatedInvite, setGeneratedInvite] = useState<GeneratedInvite | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [notesFor, setNotesFor] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
  });
  const isOperator = status.data?.isOperator ?? false;

  const portfolio = useQuery({
    queryKey: ["admin", "portfolio", "30d"],
    enabled: isOperator,
    queryFn: () => api.get<Portfolio>("/admin/portfolio", { period: "30d" }),
    staleTime: 60_000,
  });

  const provision = useMutation({
    mutationFn: (input: { name: string; ownerEmail?: string }) =>
      api.post<{
        ok: boolean;
        accountId: string;
        name: string;
        inviteUrl: string | null;
        inviteExpiresAt?: string | null;
        inviteProviderKind?: "clerk_application_invitation" | "clerk_sign_in_token" | null;
        inviteError?: string;
      }>(
        "/admin/clients/provision",
        input,
      ),
    onSuccess: (d) => {
      setName("");
      setOwnerEmail("");
      setGeneratedInvite(d.inviteUrl ? {
        url: d.inviteUrl,
        expiresAt: d.inviteExpiresAt ?? null,
        providerKind: d.inviteProviderKind ?? null,
      } : null);
      if (d.inviteError) toast.error(d.inviteError);
      else if (d.inviteUrl) toast.success(`Created “${d.name}” — invite link ready below.`);
      else toast.success(`Created “${d.name}”. It's now in your account switcher.`);
      void qc.invalidateQueries({ queryKey: ["admin", "portfolio"] });
      void qc.invalidateQueries({ queryKey: ["account", "memberships"] });
    },
    onError: () => toast.error("Couldn't create that client. Try again."),
  });

  const switchTo = async (accountId: string): Promise<void> => {
    setSwitching(accountId);
    try {
      await changeAccount({
        path: "/account/switch",
        body: { accountId },
        targetAccountId: accountId,
        destination: "/dashboard",
      });
    } catch {
      setSwitching(null);
      toast.error("Couldn't switch into that account.");
    }
  };

  const copyInvite = async (): Promise<void> => {
    if (!generatedInvite) return;
    try {
      await navigator.clipboard.writeText(generatedInvite.url);
      toast.success("Invite link copied.");
    } catch {
      toast.error("Couldn't copy — select and copy it manually.");
    }
  };

  if (status.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (!isOperator) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Clients</h1>
        <p style={{ color: "var(--body)", marginTop: 10 }}>This area is only available to agency operators.</p>
      </div>
    );
  }

  const rows = portfolio.data?.rows ?? [];

  return (
    <div>
      <div style={styles.kicker}>Agency</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Where does each client stand?</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 780, lineHeight: 1.5, marginTop: 0 }}>
        Every account you manage: setup progress, connections, cost coverage, data freshness — plus your private notes. Create a
        new client and their owner invite in one move.
      </p>

      {/* Create a client */}
      <div style={styles.card}>
        <div style={{ fontWeight: 600, color: "var(--text)", fontSize: 14, marginBottom: 4 }}>Add a new client</div>
        <div style={{ color: "var(--body)", fontSize: 12.5, marginBottom: 12 }}>
          Creates a fresh, empty account with you as owner. Add the owner's email and you'll get their invite link to send —
          they land in the account as its owner and go straight into setup.
        </div>
        <form
          style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            setGeneratedInvite(null);
            provision.mutate({ name: name.trim(), ownerEmail: ownerEmail.trim() || undefined });
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client business name (e.g. Peak Biome)" style={styles.input} maxLength={255} />
          <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="Owner's email (optional — creates the invite)" style={styles.input} maxLength={200} type="email" />
          <button type="submit" style={styles.primaryBtn} disabled={provision.isPending || !name.trim()}>
            {provision.isPending ? "Creating…" : "Create client"}
          </button>
        </form>
        {generatedInvite && (
          <div style={styles.inviteBox}>
            <div style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>Owner invite — send them this link:</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code style={styles.inviteCode}>{generatedInvite.url}</code>
              <button type="button" style={styles.switchBtn} onClick={() => void copyInvite()}>Copy</button>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--body-faint)", marginTop: 5 }}>
              {invitationExpiryCopy(generatedInvite.expiresAt)} This link works once and only for the invited email.
            </div>
          </div>
        )}
      </div>

      {/* Client list */}
      <div style={{ marginTop: 26 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 18, margin: 0 }}>Your clients</h2>
          <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
            {rows.length} {rows.length === 1 ? "account" : "accounts"}
          </span>
        </div>

        {portfolio.isLoading ? (
          <p style={{ color: "var(--body)", marginTop: 12 }}>Sizing up every account…</p>
        ) : rows.length === 0 ? (
          <p style={{ color: "var(--body)", marginTop: 12 }}>No accounts yet — add your first client above.</p>
        ) : (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {rows.map((a) => (
              <Card key={a.accountId} style={{ padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span aria-hidden="true" style={{ ...styles.swatch, background: a.themeColorHex ?? "var(--accent)" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                      <span style={{ ...styles.healthDot, background: a.health === "bad" ? "var(--bad)" : a.health === "warn" ? "#E5A83B" : "var(--good)" }} />
                      <span style={{ color: "var(--text)", fontSize: 14.5, fontWeight: 600 }}>{a.name}</span>
                      {a.isDemo && <span style={styles.demoChip}>demo</span>}
                      {!a.onboardingCompleted && !a.isDemo && <span style={styles.stageChip}>setting up · step {a.onboardingStep}</span>}
                    </div>
                    <div style={{ color: "var(--body)", fontSize: 12, marginTop: 4, display: "flex", gap: 14, flexWrap: "wrap" }}>
                      <span>{a.createdAt ? `since ${new Date(a.createdAt).toLocaleDateString()}` : ""}</span>
                      <span>
                        {a.connectionsBroken > 0 ? (
                          <span style={{ color: "var(--bad)" }}>⚠ {a.connectionsBroken} connection{a.connectionsBroken > 1 ? "s" : ""} broken</span>
                        ) : (
                          `${a.connectionsActive} connection${a.connectionsActive === 1 ? "" : "s"}`
                        )}
                      </span>
                      <span>
                        {a.costsCoveragePct === null
                          ? "no products yet"
                          : a.costsCoveragePct >= 1
                            ? "costs: full coverage"
                            : `costs: ${Math.round(a.costsCoveragePct * 100)}% of products`}
                      </span>
                      <span>
                        {a.connectionsActive === 0
                          ? "no data source"
                          : a.syncStale
                            ? <span style={{ color: "#B07E18" }}>data stale</span>
                            : a.lastSyncAt
                              ? `synced ${freshness(a.lastSyncAt)}`
                              : "first sync pending"}
                      </span>
                    </div>
                  </div>
                  <button type="button" style={styles.switchBtn} onClick={() => setNotesFor(notesFor === a.accountId ? null : a.accountId)}>
                    {notesFor === a.accountId ? "Hide notes" : "Notes"}
                  </button>
                  <button type="button" style={styles.switchBtn} disabled={switching !== null} onClick={() => void switchTo(a.accountId)}>
                    {switching === a.accountId ? "Switching…" : "Switch in →"}
                  </button>
                </div>
                {notesFor === a.accountId && <ClientNote accountId={a.accountId} />}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Private per-client note — loads on open, saves on demand. Operator-only. */
function ClientNote({ accountId }: { accountId: string }): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const [draft, setDraft] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin", "client-note", accountId],
    queryFn: () => api.get<{ body: string; updatedAt: string | null }>(`/admin/clients/${accountId}/note`),
  });

  const save = useMutation({
    mutationFn: (body: string) => api.put(`/admin/clients/${accountId}/note`, { body }),
    onSuccess: () => toast.success("Note saved."),
    onError: () => toast.error("Couldn't save the note."),
  });

  const value = draft ?? q.data?.body ?? "";

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
      <textarea
        value={value}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Operator notes on this client — goals, quirks, promises made. Shared between agency operators; the client never sees it."
        style={styles.noteArea}
        rows={4}
        disabled={q.isLoading}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <span style={{ fontSize: 11.5, color: "var(--body-faint)" }}>
          {q.data?.updatedAt ? `Last saved ${new Date(q.data.updatedAt).toLocaleString()}` : ""}
        </span>
        <button
          type="button"
          style={styles.primaryBtn}
          disabled={save.isPending || draft === null || draft === (q.data?.body ?? "")}
          onClick={() => save.mutate(value)}
        >
          {save.isPending ? "Saving…" : "Save note"}
        </button>
      </div>
    </div>
  );
}

function freshness(iso: string): string {
  const hours = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return "<1h ago";
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const styles: Record<string, React.CSSProperties> = {
  kicker: { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" },
  card: {
    marginTop: 22,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r-card)",
    boxShadow: "var(--shadow-card)",
    padding: "18px 20px",
  },
  input: {
    flex: 1,
    minWidth: 220,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r-sm)",
    padding: "9px 12px",
    fontSize: 13.5,
    color: "var(--text)",
    fontFamily: "inherit",
  },
  primaryBtn: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    borderRadius: "var(--r-sm)",
    padding: "9px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  inviteBox: {
    marginTop: 12,
    padding: "10px 13px",
    background: "color-mix(in srgb, var(--accent) 6%, transparent)",
    border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
    borderRadius: "var(--r-sm)",
  },
  inviteCode: {
    fontSize: 12,
    color: "var(--text)",
    background: "var(--bg)",
    padding: "5px 9px",
    borderRadius: 6,
    border: "1px solid var(--border)",
    wordBreak: "break-all",
    flex: 1,
    minWidth: 220,
  },
  swatch: { width: 12, height: 12, borderRadius: 4, flexShrink: 0 },
  healthDot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  demoChip: {
    fontSize: 10.5,
    fontWeight: 600,
    padding: "1px 8px",
    borderRadius: 10,
    background: "var(--surface-2)",
    color: "var(--body)",
    border: "1px solid var(--border)",
  },
  stageChip: {
    fontSize: 10.5,
    fontWeight: 600,
    padding: "1px 8px",
    borderRadius: 10,
    background: "color-mix(in srgb, var(--accent) 14%, transparent)",
    color: "var(--accent)",
  },
  switchBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    color: "var(--accent)",
    borderRadius: "var(--r-sm)",
    padding: "7px 13px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  noteArea: {
    width: "100%",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r-sm)",
    padding: "9px 12px",
    fontSize: 13,
    color: "var(--text)",
    fontFamily: "inherit",
    lineHeight: 1.5,
    resize: "vertical",
    boxSizing: "border-box",
  },
};
